import { Chess, Square } from "chess.js";

interface ChessPuzzle {
    title: string;
    fen: string;
    pgn: string;
    moves: string[];
    playerColor: "w" | "b";
}

/**
 * Replays a PGN up to `initialPly` half-moves and returns the FEN at that position.
 * Exported for testing.
 */
export function getPuzzleFenFromPgn(pgn: string, initialPly: number): string {
    const loader = new Chess();
    loader.loadPgn(pgn);
    const moves = loader.history({ verbose: true });
    const game = new Chess();
    for (let i = 0; i < Math.min(initialPly, moves.length); i++) {
        game.move(moves[i]);
    }
    return game.fen();
}

/**
 * Converts an array of UCI moves (e.g. "f3e5") to SAN (e.g. "Nxe5") by
 * replaying them sequentially from startFen. Exported for testing.
 */
export function convertUciMovesToSan(startFen: string, uciMoves: string[]): string[] {
    const game = new Chess(startFen);
    return uciMoves.map((uci) => {
        try {
            const move = game.move({
                from: uci.slice(0, 2) as Square,
                to: uci.slice(2, 4) as Square,
                promotion: uci[4] || undefined,
            });
            if (!move) throw new Error(`Invalid UCI move: ${uci}`);
            return move.san;
        } catch {
            throw new Error(`Invalid UCI move: ${uci}`);
        }
    });
}

/**
 * Maps a raw Lichess puzzle API response to the ChessPuzzle interface.
 */
function mapLichessResponse(data: any, defaultTitle: string): ChessPuzzle {
    const startFen = getPuzzleFenFromPgn(data.game.pgn, data.puzzle.initialPly);
    const sanMoves = convertUciMovesToSan(startFen, data.puzzle.solution);
    const playerColor = startFen.split(" ")[1] as "w" | "b";

    return {
        title: data.puzzle.id ? `Lichess Puzzle #${data.puzzle.id}` : defaultTitle,
        fen: startFen,
        pgn: data.game.pgn,
        moves: sanMoves,
        playerColor,
    };
}

/**
 * Fetches the daily puzzle from Lichess API
 * @returns Promise with puzzle data or null if fetch fails
 */
export async function fetchDailyPuzzle(): Promise<ChessPuzzle | null> {
    try {
        const response = await fetch("https://lichess.org/api/puzzle/daily");

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return mapLichessResponse(data, "Daily Puzzle");
    } catch (error) {
        console.error("Error fetching daily puzzle:", error);
        return null;
    }
}

/**
 * Caches puzzle data in memory with timestamp
 */
let cachedPuzzle: { puzzle: ChessPuzzle; timestamp: number } | null = null;
const CACHE_DURATION = 1000 * 60 * 60; // 1 hour cache

export async function getDailyPuzzle(): Promise<ChessPuzzle | null> {
    const now = Date.now();

    if (cachedPuzzle && now - cachedPuzzle.timestamp < CACHE_DURATION) {
        return cachedPuzzle.puzzle;
    }

    const puzzle = await fetchDailyPuzzle();

    if (puzzle) cachedPuzzle = { puzzle, timestamp: now };

    return puzzle;
}

/**
 * Fetches a random puzzle from Lichess API
 * @returns Promise with puzzle data or null if fetch fails
 */
export async function fetchRandomPuzzle(): Promise<ChessPuzzle | null> {
    try {
        const response = await fetch("https://lichess.org/api/puzzle/next");

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return mapLichessResponse(data, "Chess Puzzle");
    } catch (error) {
        console.error("Error fetching random puzzle:", error);
        return null;
    }
}

/**
 * Fallback puzzle in case API fails
 * Player should be white in this puzzle (FEN has 'w' to move)
 */
export const FALLBACK_PUZZLE: ChessPuzzle = {
    title: "Practice Puzzle",
    fen: "r1bqkbnr/pppp1ppp/2n5/4p3/1b2P3/2NP1N2/PPP2PPP/R1BQKB1R w KQkq - 0 1",
    pgn: "1. Nxe5 Nxe5 2. d4 Bxc3+ 3. bxc3",
    moves: ["Nxe5", "Nxe5", "d4", "Bxc3+", "bxc3"],
    playerColor: "w", // MATCHES FEN: white moves first
};
